import { TIME_ZONES, type TimeZone } from '@eklevo/shared/domain'
import { LOCALES, type Locale } from '@eklevo/shared/i18n/backoffice'
import { UPLOAD_RULES, uploadProblem } from '@eklevo/shared/uploads'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { ImageIcon } from 'lucide-react'
import {
  useCallback,
  useEffect,
  useId,
  useRef,
  useState,
  useSyncExternalStore,
  type FormEvent,
} from 'react'
import { Button } from '../../components/ui/button'
import { Card } from '../../components/ui/card'
import { Field } from '../../components/ui/field'
import { Select } from '../../components/ui/select'
import { TextInput } from '../../components/ui/text-input'
import { FileButton, UploadStatus, type UploadTexts } from '../../components/ui/upload'
import { errorCode, errorIssues, errorReason, sessionGone } from '../../lib/errors'
import { sendUpload, UploadTracker, type UploadState } from '../../lib/uploads'
import {
  checkEdit,
  editChanges,
  editDraftOf,
  editIssuesToErrors,
  shownEditErrors,
  toChange,
  type EditDraft,
} from '../lib/company-edit'
import { INPUT_BOUNDS, logoError, type FieldErrors } from '../lib/company-wizard'
import { staffUploads } from '../lib/editor'
import { useI18n } from '../lib/i18n'
import {
  companyGone,
  companyKey,
  organizationChanged,
  type CompanyDetail,
  type Reopen,
} from '../lib/screens'
import { forCompany, useTRPC, useTRPCClient } from '../lib/trpc'
import { PasskeyNote } from './passkey-note'
import { useReauth } from './reauth'
import { BrandFields, LegalFields } from './wizard/company-step'
import { useFieldError } from './wizard/step'
import { Swatch } from './wizard/summary-step'

/*
 * A company's details on its page (ADR 0009 §8; PRD 04 §2.2's «Editar»): its legal data (decided
 * on 2026-10-18), default language, time zone, brand colours and logo, and
 * «Editar», a form in their place with its name too, which saves them with the passkey again if
 * it was not touched in the last minutes (ADR 0009 §1). The same fields, rules and messages as the
 * wizard's first step (ADR 0009 §7): the contrast of each use of the colours (ADR 0007 §5), and
 * the logo uploaded from the browser as soon as it is chosen (ADR 0009 §6). It saves over the
 * company's data as the form found them: if another operator saved them meanwhile, nothing is
 * saved, the form closes and the page shows them as they are now. The company's colours show here
 * as swatches, as data: the panel itself keeps Eklevo's brand. Not in the Figma: marked for design
 * review.
 */
export function CompanyDataSection({
  company,
  reopen,
}: {
  company: CompanyDetail
  /** Another tab opened another company: the way to open this one again. */
  reopen: Reopen
}) {
  const { t } = useI18n()
  const id = useId()
  const [editing, setEditing] = useState(false)
  const [outcome, setOutcome] = useState<'saved' | 'stale' | null>(null)
  const editButton = useRef<HTMLButtonElement>(null)
  // Where the focus goes once the form closes: back to «Editar».
  const refocus = useRef(false)
  useEffect(() => {
    if (editing || !refocus.current) return
    refocus.current = false
    editButton.current?.focus()
  }, [editing])
  return (
    <section aria-labelledby={id}>
      <Card>
        <div className="flex flex-wrap items-start justify-between gap-3">
          <h2 id={id} className="text-lg font-medium">
            {t(editing ? 'company.edit.title' : 'company.data.title')}
          </h2>
          {!editing && (
            <Button
              ref={editButton}
              variant="secondary"
              size="sm"
              onClick={() => {
                setOutcome(null)
                setEditing(true)
              }}
            >
              {t('company.edit')}
            </Button>
          )}
        </div>
        <p role="status" className="text-sm text-success not-empty:mt-3">
          {outcome === 'saved' ? t('company.edit.saved') : ''}
        </p>
        <p role="alert" className="text-sm text-danger not-empty:mt-3">
          {outcome === 'stale' ? t('company.edit.stale') : ''}
        </p>
        {editing ? (
          <CompanyEditForm
            company={company}
            reopen={reopen}
            onDone={(done) => {
              setOutcome(done === 'cancelled' ? null : done)
              refocus.current = true
              setEditing(false)
              // The company's data as they are now, instead of the ones the form started from.
              if (done === 'stale') void reopen()
            }}
          />
        ) : (
          <CompanyDataView company={company} />
        )}
      </Card>
    </section>
  )
}

/**
 * The company's logo, from the back office's own route for the company open (decided on 2026-11-02),
 * each time it is served an entry in the company's trail. The address says which logo the page
 * expects: if another tab has opened another company, it is not found, and the alternative text
 * says whose it was.
 */
function CompanyLogo({ company }: { company: CompanyDetail }) {
  const { t } = useI18n()
  return (
    <img
      src={`/api/staff/company-logo?v=${company.logoId}`}
      alt={t('company.data.logo.alt', { company: company.name })}
      className="h-12 max-w-48 rounded-lg border border-line bg-surface object-contain p-1"
    />
  )
}

/** The legal data, in the order «Editar» asks for them. */
const LEGAL_VIEW = ['legalName', 'taxId', 'privacyContact', 'address'] as const

function CompanyDataView({ company }: { company: CompanyDetail }) {
  const { t } = useI18n()
  return (
    <dl className="mt-4 grid gap-x-4 gap-y-3 sm:grid-cols-2">
      {LEGAL_VIEW.map((field) => (
        <div key={field} className="min-w-0">
          <dt className="text-xs text-ink-muted">{t(`company.data.${field}`)}</dt>
          <dd className="[overflow-wrap:anywhere]">
            {company[field] ?? <span className="text-ink-muted">{t('company.data.none')}</span>}
          </dd>
        </div>
      ))}
      <div className="min-w-0">
        <dt className="text-xs text-ink-muted">{t('company.data.locale')}</dt>
        <dd>{t(`wizard.locale.${company.defaultLocale}`)}</dd>
      </div>
      <div className="min-w-0">
        <dt className="text-xs text-ink-muted">{t('company.data.timeZone')}</dt>
        <dd>{t(`wizard.timeZone.${company.timeZone}`)}</dd>
      </div>
      <div className="min-w-0">
        <dt className="text-xs text-ink-muted">{t('company.data.brand')}</dt>
        <dd>
          {company.brand ? (
            <span className="flex flex-wrap gap-x-4 gap-y-1">
              <Swatch color={company.brand.primary} label={t('wizard.brand.primary')} />
              <Swatch color={company.brand.accent} label={t('wizard.brand.accent')} />
            </span>
          ) : (
            t('company.data.brand.none')
          )}
        </dd>
      </div>
      <div className="min-w-0">
        <dt className="text-xs text-ink-muted">{t('company.data.logo')}</dt>
        <dd>{company.logoId ? <CompanyLogo company={company} /> : t('company.data.logo.none')}</dd>
      </div>
    </dl>
  )
}

/**
 * «Editar»: the form, which starts from the company as it is and lives only in the page. Saving
 * checks it first with the server's rules, says so if nothing changed, and then asks for the
 * passkey if needed and saves it, over the version of the data the form started from; a problem
 * the server answers stays at its field until it changes. While it saves, the form stays.
 */
function CompanyEditForm({
  company,
  reopen,
  onDone,
}: {
  company: CompanyDetail
  reopen: Reopen
  /**
   * The form closes: saved; cancelled; or not saved because another operator saved the company's
   * data after the form opened.
   */
  onDone: (outcome: 'saved' | 'cancelled' | 'stale') => void
}) {
  const { t } = useI18n()
  const client = useTRPCClient()
  const trpc = useTRPC()
  const queryClient = useQueryClient()
  const reauth = useReauth()
  // The company as the form found it: what it saves over (its version), even if the page asks
  // for the company again meanwhile.
  const [start] = useState(company)
  const [draft, setDraft] = useState<EditDraft>(() => editDraftOf(company))
  const [pageErrors, setPageErrors] = useState<FieldErrors>({})
  const [serverErrors, setServerErrors] = useState<FieldErrors>({})
  const [failure, setFailure] = useState<{ text: string; reopen?: boolean } | null>(null)
  const saveButton = useRef<HTMLButtonElement>(null)
  const form = useRef<HTMLFormElement>(null)
  // The form takes the place of «Editar», which had the focus: it goes to the first field.
  const nameInput = useRef<HTMLInputElement>(null)
  useEffect(() => nameInput.current?.focus(), [])
  const problem = useRef<HTMLParagraphElement>(null)
  const [focusProblem, setFocusProblem] = useState(0)
  useEffect(() => {
    if (focusProblem === 0) return
    ;(form.current?.querySelector<HTMLElement>('[aria-invalid="true"]') ?? problem.current)?.focus()
  }, [focusProblem])

  const change = useCallback((update: (d: EditDraft) => EditDraft, fields: readonly string[]) => {
    setDraft(update)
    const without = (current: FieldErrors) => {
      if (!fields.some((f) => f in current)) return current
      const next = { ...current }
      for (const f of fields) delete next[f]
      return next
    }
    setPageErrors(without)
    setServerErrors(without)
    setFailure(null)
  }, [])

  // The logo goes as soon as it is chosen (ADR 0009 §6); its token, once clean, is the form's.
  const [uploads] = useState(() => new UploadTracker())
  useEffect(() => () => uploads.stopAll(), [uploads])
  const logoState = useSyncExternalStore(uploads.subscribe, uploads.getSnapshot).get('logo')
  const initialLogo = editDraftOf(start).logo
  const chooseLogo = (file: File) => {
    uploads.clear('logo')
    const refused = uploadProblem('logo', file)
    if (refused) {
      uploads.refuse('logo', file.name, refused)
      return
    }
    change((d) => ({ ...d, logo: { status: 'uploading', file: file.name } }), ['logo'])
    // Another file, even of the same name, or «Cancelar» stops this one: what it ends with is not
    // the form's any more.
    uploads.start('logo', file, async (opts) => {
      const still = (d: EditDraft) =>
        !opts.signal.aborted && d.logo?.status === 'uploading' && d.logo.file === file.name
      try {
        const token = await sendUpload(staffUploads(client), 'logo', file, opts)
        change(
          (d) => (still(d) ? { ...d, logo: { status: 'ready', file: file.name, token } } : d),
          ['logo'],
        )
      } catch (err) {
        change((d) => (still(d) ? { ...d, logo: initialLogo } : d), [])
        throw err
      }
    })
  }
  const logo: EditLogoUpload = {
    state: logoState,
    choose: chooseLogo,
    cancel: () => {
      uploads.cancel('logo')
      change((d) => (d.logo?.status === 'uploading' ? { ...d, logo: initialLogo } : d), ['logo'])
    },
    remove: () => {
      uploads.clear('logo')
      change((d) => ({ ...d, logo: null }), ['logo'])
    },
    keep: () => change((d) => ({ ...d, logo: { status: 'kept' } }), ['logo']),
  }

  const save = useMutation({
    mutationFn: (sent: EditDraft) =>
      reauth.run(
        () => client.companies.update.mutate(toChange(sent, start), forCompany(company.id)),
        {
          from: saveButton.current,
          purpose: t('company.edit.purpose', { company: company.name }),
        },
      ),
    gcTime: 0,
    onSuccess: (result) => {
      // Cancelled at the passkey: nothing done, nothing to say.
      if (!result) return
      queryClient.setQueryData<CompanyDetail>(
        companyKey(company.id),
        (d) => d && { ...d, ...result },
      )
      void queryClient.invalidateQueries({ queryKey: trpc.companies.list.queryKey() })
      onDone('saved')
    },
    onError: (err) => {
      if (sessionGone(err)) return
      const reason = errorReason(err)
      const issues = errorIssues(err)
      if (reason === 'invalid' && issues.length > 0) {
        const { errors: found, unknown } = editIssuesToErrors(issues)
        setServerErrors(found)
        if (unknown > 0 && Object.keys(found).length === 0)
          setFailure({ text: t('common.error.generic') })
        setFocusProblem((n) => n + 1)
      } else if (reason === 'stale') onDone('stale')
      else if (reason === 'unchanged') setFailure({ text: t('company.edit.unchanged') })
      else if (organizationChanged(err))
        setFailure({ text: t('common.error.organizationChanged'), reopen: true })
      else if (companyGone(err)) setFailure({ text: t('company.notFound.title') })
      else if (errorCode(err) === 'TOO_MANY_REQUESTS')
        setFailure({ text: t('common.error.rateLimited') })
      else setFailure({ text: t('common.error.generic') })
    },
  })

  const submit = (e: FormEvent) => {
    e.preventDefault()
    if (save.isPending) return
    setFailure(null)
    const found = checkEdit(draft)
    setPageErrors(found)
    if (Object.keys(shownEditErrors(draft, { ...found, ...serverErrors })).length > 0) {
      setFocusProblem((n) => n + 1)
      return
    }
    if (!editChanges(draft, start)) {
      setFailure({ text: t('company.edit.unchanged') })
      return
    }
    save.mutate(draft)
  }

  const errors = shownEditErrors(draft, { ...pageErrors, ...serverErrors })
  const error = useFieldError(errors)
  const hasProblems = Object.keys(errors).length > 0
  return (
    <form ref={form} noValidate onSubmit={submit} className="mt-4 space-y-6">
      <p className="text-sm text-ink-muted">{t('company.edit.intro')}</p>
      <p
        ref={problem}
        role="alert"
        tabIndex={-1}
        className="text-sm text-danger outline-none not-empty:rounded-lg not-empty:bg-danger/5 not-empty:px-4 not-empty:py-3"
      >
        {hasProblems ? t('company.edit.problems') : ''}
      </p>
      <div className="grid gap-4 md:grid-cols-2">
        <Field
          label={t('wizard.company.name')}
          required
          hint={t('wizard.company.name.hint')}
          error={error('name')}
          className="md:col-span-2"
        >
          <TextInput
            ref={nameInput}
            value={draft.name}
            maxLength={INPUT_BOUNDS.line}
            autoComplete="off"
            onChange={(e) => {
              const name = e.target.value
              change((d) => ({ ...d, name }), ['name'])
            }}
          />
        </Field>
        <Field
          label={t('wizard.company.locale')}
          required
          hint={t('wizard.company.locale.hint')}
          error={error('defaultLocale')}
        >
          <Select
            value={draft.defaultLocale}
            onChange={(e) => {
              const defaultLocale = e.target.value as Locale
              change((d) => ({ ...d, defaultLocale }), ['defaultLocale'])
            }}
          >
            {LOCALES.map((l) => (
              <option key={l} value={l}>
                {t(`wizard.locale.${l}`)}
              </option>
            ))}
          </Select>
        </Field>
        <Field
          label={t('wizard.company.timeZone')}
          required
          hint={t('wizard.company.timeZone.hint')}
          error={error('timeZone')}
        >
          <Select
            value={draft.timeZone}
            onChange={(e) => {
              const timeZone = e.target.value as TimeZone
              change((d) => ({ ...d, timeZone }), ['timeZone'])
            }}
          >
            {TIME_ZONES.map((zone) => (
              <option key={zone} value={zone}>
                {t(`wizard.timeZone.${zone}`)}
              </option>
            ))}
          </Select>
        </Field>
      </div>

      <LegalFields draft={draft} errors={errors} change={change} hint="company.edit.legal.hint" />

      <EditLogoField draft={draft} company={start} error={error('logo')} logo={logo} />

      <BrandFields draft={draft} errors={errors} change={change} />

      <PasskeyNote className="text-sm text-ink-muted" />
      <div>
        <p role="alert" className="text-sm text-danger">
          {failure?.text ?? ''}
        </p>
        {failure?.reopen && (
          <Button variant="secondary" size="sm" className="mt-3" onClick={() => void reopen()}>
            {t('company.reopen')}
          </Button>
        )}
      </div>
      <div className="flex flex-wrap justify-end gap-3 border-t border-line pt-6">
        <Button
          variant="ghost"
          onClick={() => {
            if (save.isPending) return
            uploads.stopAll()
            onDone('cancelled')
          }}
        >
          {t('common.cancel')}
        </Button>
        <Button
          ref={saveButton}
          type="submit"
          // While it runs the button says so and waits, keeping the focus.
          aria-disabled={save.isPending || undefined}
          className="aria-disabled:cursor-progress aria-disabled:opacity-50"
        >
          {save.isPending ? t('company.edit.busy') : t('company.edit.save')}
        </Button>
      </div>
    </form>
  )
}

/** The form's logo upload: where it stands, and what the field does. */
interface EditLogoUpload {
  state: UploadState | undefined
  choose: (file: File) => void
  cancel: () => void
  remove: () => void
  /** Keeps the company's logo after all. */
  keep: () => void
}

/**
 * The logo of «Editar»: the company's, kept until another one is chosen or it is taken away; a new
 * one, uploaded and analysed before it is the form's (PNG or JPEG, up to 1 MB and 32 to 4,000
 * pixels a side, ADR 0007); or none. Each of these shows other buttons: when the one that had the
 * focus goes with what it showed («Quitar logo», «Mantener el logo», a file chosen, «Cancelar», an
 * upload that ends), the focus goes to what the field shows now (WCAG 2.4.3), never back to the
 * top of the page.
 */
function EditLogoField({
  draft,
  company,
  error,
  logo,
}: {
  draft: EditDraft
  company: CompanyDetail
  error: string | null
  logo: EditLogoUpload
}) {
  const { t } = useI18n()
  const id = useId()
  const texts: UploadTexts = {
    sending: (file) => t('upload.sending', { file }),
    percent: (percent) => t('upload.percent', { percent: percent / 100 }),
    progress: t('upload.progress'),
    scanning: (file) => t('upload.scanning', { file }),
    cancel: t('upload.cancel'),
    cancelLabel: (file) => t('upload.cancel.label', { file }),
    failed: (reason) => t(logoError(reason)),
  }
  const current = draft.logo
  const busy = logo.state && logo.state.status !== 'failed'
  // What the field shows: the logo kept or ready, an upload going, the logo to be taken away, or
  // only «Elegir logo».
  const shown = busy
    ? 'busy'
    : current?.status === 'kept'
      ? 'kept'
      : current?.status === 'ready'
        ? `ready:${current.file}`
        : current === null && company.logo
          ? 'removed'
          : 'none'
  const section = useRef<HTMLElement>(null)
  const lastShown = useRef(shown)
  useEffect(() => {
    if (lastShown.current === shown) return
    lastShown.current = shown
    // Somewhere else on the page (another field), the focus stays where it is.
    const active = document.activeElement
    if (active && active !== document.body && active.isConnected) return
    section.current
      ?.querySelector<HTMLElement>('[data-logo-focus], [data-logo-upload] button')
      ?.focus()
  }, [shown])
  const choose = (label: string, describe = false) => (
    <FileButton
      variant="secondary"
      size="sm"
      accept={UPLOAD_RULES.logo.accept}
      aria-describedby={describe ? `${id}-hint` : undefined}
      aria-invalid={error ? true : undefined}
      data-logo-focus
      onFile={logo.choose}
    >
      {label}
    </FileButton>
  )
  return (
    <section ref={section} aria-labelledby={id} className="space-y-2" data-field="logo">
      <h3 id={id} className="text-sm font-medium">
        {t('wizard.logo')}
      </h3>
      <p id={`${id}-hint`} className="text-xs text-ink-muted">
        {t('wizard.logo.hint')}
      </p>
      {current?.status === 'kept' || current?.status === 'ready' ? (
        <div className="flex flex-wrap items-center gap-3 rounded-lg border border-line p-3 text-sm">
          {current.status === 'kept' && company.logoId ? (
            <CompanyLogo company={company} />
          ) : (
            <ImageIcon aria-hidden className="size-5 shrink-0 text-ink-muted" />
          )}
          <p
            tabIndex={-1}
            data-logo-focus
            className="min-w-0 flex-1 outline-none [overflow-wrap:anywhere]"
          >
            {current.status === 'kept'
              ? t('company.edit.logo.kept')
              : t('wizard.logo.ready', { file: current.file })}
          </p>
          {choose(t('wizard.logo.change'))}
          <Button variant="secondary" size="sm" onClick={logo.remove}>
            {t('wizard.logo.remove')}
          </Button>
        </div>
      ) : (
        !busy && (
          <div className="flex flex-wrap items-center gap-3">
            {current === null && company.logo && (
              <p tabIndex={-1} data-logo-focus className="text-sm outline-none">
                {t('company.edit.logo.removed')}
              </p>
            )}
            {choose(t('wizard.logo.choose'), true)}
            {current === null && company.logo && (
              <Button variant="ghost" size="sm" onClick={logo.keep}>
                {t('company.edit.logo.undo')}
              </Button>
            )}
          </div>
        )
      )}
      <div data-logo-upload className="empty:hidden">
        <UploadStatus state={logo.state} texts={texts} onCancel={logo.cancel} />
      </div>
      {error && logo.state?.status !== 'failed' && (
        <p role="alert" className="text-sm text-danger">
          {error}
        </p>
      )}
    </section>
  )
}
